import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus, Trash2, CheckCircle2 } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { WeightInput } from '../../components/ui/WeightInput.jsx';

const ITEM_STATUS_LABEL = { ok: 'OK', short: 'Short', damaged: 'Damaged', excess: 'Unexpected / excess' };

function today() {
  return new Date().toISOString().slice(0, 10);
}

function extraItemRow() {
  return {
    key: crypto.randomUUID(),
    issueItemId: null,
    itemName: '',
    serialNumber: '',
    purityId: '',
    purityLabel: '',
    grossWeight: '0',
    netWeight: '0',
    quantity: '1',
    status: 'excess',
    remarks: '',
  };
}

export default function ReceiptForm() {
  const { id } = useParams(); // issue_id
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [voucherNo, setVoucherNo] = useState('');
  const [receiptDate, setReceiptDate] = useState(today());
  const [receivedFrom, setReceivedFrom] = useState('');
  const [receivedFromAddress, setReceivedFromAddress] = useState('');
  const [rows, setRows] = useState([]); // pending lines, pre-filled and toggleable
  const [extraRows, setExtraRows] = useState([]); // unexpected items
  const [remarks, setRemarks] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const { data: voucher, isLoading } = useQuery({
    queryKey: ['issues', id],
    queryFn: () => api.get(`/issue/${id}`),
  });

  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });

  useEffect(() => {
    if (!voucher) return;
    // The voucher number defaults to matching this receipt's issue voucher —
    // same challan-book pairing the counter uses on paper — but stays
    // editable below. Purity defaults per line from what was issued.
    setVoucherNo(voucher.voucher_no);
    setReceivedFrom(voucher.issued_to_name);
    setReceivedFromAddress(voucher.issued_to_address ?? '');
    setRows(
      voucher.items
        .filter((item) => item.pending_quantity > 0)
        .map((item) => {
          const grossPerUnit = item.quantity > 0 ? Number(item.gross_weight) / item.quantity : 0;
          return {
            issueItemId: item.item_id,
            itemName: item.item_name,
            serialNumber: item.serial_number,
            purityId: item.purity_id ? String(item.purity_id) : '',
            purityLabel: item.purity_label ?? '',
            included: true,
            pendingQuantity: item.pending_quantity,
            pendingNetWeight: item.pending_net_weight,
            quantity: String(item.pending_quantity),
            grossWeight: String((grossPerUnit * item.pending_quantity).toFixed(3)),
            netWeight: String(item.pending_net_weight),
            status: 'ok',
            remarks: '',
          };
        }),
    );
  }, [voucher]);

  const updateRow = (issueItemId, patch) =>
    setRows((r) => r.map((row) => (row.issueItemId === issueItemId ? { ...row, ...patch } : row)));

  const applyRowPurity = (issueItemId, value) => {
    const purity = (puritiesQuery.data ?? []).find((p) => String(p.id) === value);
    updateRow(issueItemId, { purityId: value, purityLabel: purity ? `${purity.metal} ${purity.label}` : '' });
  };

  const updateExtra = (key, patch) => setExtraRows((r) => r.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const removeExtra = (key) => setExtraRows((r) => r.filter((row) => row.key !== key));
  const addExtra = () => setExtraRows((r) => [...r, extraItemRow()]);

  const applyExtraPurity = (key, value) => {
    const purity = (puritiesQuery.data ?? []).find((p) => String(p.id) === value);
    updateExtra(key, { purityId: value, purityLabel: purity ? `${purity.metal} ${purity.label}` : '' });
  };

  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/receipt/create', payload),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      navigate(`/issue-receipt/receipts/${result.receipt_id}`, { state: { justCreated: true } });
    },
    onError: (err) => {
      setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this receipt. Please try again.');
    },
  });

  if (isLoading || !voucher) return null;

  const includedRows = rows.filter((r) => r.included && Number(r.quantity) > 0);
  const canSubmit =
    !!receiptDate &&
    receivedFromAddress.trim().length >= 3 &&
    includedRows.length + extraRows.length > 0 &&
    includedRows.every((r) => r.purityLabel.trim()) &&
    extraRows.every((r) => r.itemName.trim() && r.purityLabel.trim());

  const onSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    createMutation.mutate({
      issueId: Number(id),
      voucherNo: voucherNo.trim() || undefined,
      receiptDate,
      receivedFrom: receivedFrom || undefined,
      receivedFromAddress,
      remarks: remarks || undefined,
      items: [
        ...includedRows.map((r) => ({
          issueItemId: r.issueItemId,
          purityId: r.purityId ? Number(r.purityId) : undefined,
          purityLabel: r.purityLabel,
          grossWeight: Number(r.grossWeight),
          netWeight: Number(r.netWeight),
          quantity: Number(r.quantity),
          status: r.status,
          remarks: r.remarks || undefined,
        })),
        ...extraRows.map((r) => ({
          itemName: r.itemName,
          serialNumber: r.serialNumber || undefined,
          purityId: r.purityId ? Number(r.purityId) : undefined,
          purityLabel: r.purityLabel,
          grossWeight: Number(r.grossWeight),
          netWeight: Number(r.netWeight),
          quantity: Number(r.quantity),
          status: r.status,
          remarks: r.remarks || undefined,
        })),
      ],
    });
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5 pb-28">
      <div className="flex items-center gap-3">
        <Link to={`/issue-receipt/${id}`} className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Record receipt</h1>
          <p className="text-sm text-muted">Against issue voucher {voucher.voucher_no}</p>
        </div>
      </div>

      <section className="card grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
        <TextField
          label="Voucher number"
          value={voucherNo}
          onChange={(e) => setVoucherNo(e.target.value)}
          hint="Defaults to the issue voucher's number — change it if this slip is numbered differently"
        />
        <TextField label="Date of receipt" type="date" required value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} />
        <TextField label="Received from" required value={receivedFrom} onChange={(e) => setReceivedFrom(e.target.value)} />
        <TextField label="Address" required value={receivedFromAddress} onChange={(e) => setReceivedFromAddress(e.target.value)} />
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Pending items</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-muted">Nothing is pending on this voucher.</p>
        ) : (
          rows.map((row) => (
            <div key={row.issueItemId} className={`card flex flex-col gap-3 p-4 ${row.included ? '' : 'opacity-60'}`}>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={row.included} onChange={(e) => updateRow(row.issueItemId, { included: e.target.checked })} />
                <span className="font-medium">{row.itemName}</span>
                {row.serialNumber ? <span className="text-sm text-muted">· {row.serialNumber}</span> : null}
                <span className="ml-auto text-sm text-muted">
                  Pending: {row.pendingQuantity} · {formatWeight(row.pendingNetWeight)}
                </span>
              </label>
              {row.included ? (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <TextField
                      label="Quantity received"
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max={row.pendingQuantity}
                      value={row.quantity}
                      onChange={(e) => updateRow(row.issueItemId, { quantity: e.target.value })}
                    />
                    <SelectField label="Condition" value={row.status} onChange={(e) => updateRow(row.issueItemId, { status: e.target.value })}>
                      {Object.entries(ITEM_STATUS_LABEL).filter(([v]) => v !== 'excess').map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </SelectField>
                  </div>
                  <SelectField label="Purity of gold / silver" required value={row.purityId} onChange={(e) => applyRowPurity(row.issueItemId, e.target.value)}>
                    <option value="">Select purity</option>
                    {(puritiesQuery.data ?? []).map((p) => (
                      <option key={p.id} value={p.id}>{p.metal} {p.label}</option>
                    ))}
                  </SelectField>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <WeightInput label="Gross weight received" value={row.grossWeight} onChange={(v) => updateRow(row.issueItemId, { grossWeight: v })} />
                    <WeightInput label="Net weight received" value={row.netWeight} onChange={(v) => updateRow(row.issueItemId, { netWeight: v })} />
                  </div>
                  <TextField label="Remarks" value={row.remarks} onChange={(e) => updateRow(row.issueItemId, { remarks: e.target.value })} />
                </>
              ) : null}
            </div>
          ))
        )}
      </section>

      <section className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Unexpected items</h2>
          <Button type="button" variant="secondary" size="sm" onClick={addExtra}>
            <Plus size={15} aria-hidden="true" /> Add item
          </Button>
        </div>
        {extraRows.map((row) => (
          <div key={row.key} className="card flex flex-col gap-3 p-4">
            <div className="flex items-start gap-2">
              <div className="flex-1">
                <TextField label="Item name" required value={row.itemName} onChange={(e) => updateExtra(row.key, { itemName: e.target.value })} />
              </div>
              <button type="button" onClick={() => removeExtra(row.key)} className="mt-7 flex h-touch w-touch shrink-0 items-center justify-center rounded-card text-muted hover:bg-danger/10 hover:text-danger" aria-label="Remove item">
                <Trash2 size={18} aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TextField label="Serial number" value={row.serialNumber} onChange={(e) => updateExtra(row.key, { serialNumber: e.target.value })} />
              <TextField label="Quantity" type="number" inputMode="numeric" min="1" value={row.quantity} onChange={(e) => updateExtra(row.key, { quantity: e.target.value })} />
            </div>
            <SelectField label="Purity of gold / silver" required value={row.purityId} onChange={(e) => applyExtraPurity(row.key, e.target.value)}>
              <option value="">Select purity</option>
              {(puritiesQuery.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>{p.metal} {p.label}</option>
              ))}
            </SelectField>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <WeightInput label="Gross weight" value={row.grossWeight} onChange={(v) => updateExtra(row.key, { grossWeight: v })} />
              <WeightInput label="Net weight" value={row.netWeight} onChange={(v) => updateExtra(row.key, { netWeight: v })} />
            </div>
            <TextField label="Remarks" value={row.remarks} onChange={(e) => updateExtra(row.key, { remarks: e.target.value })} />
          </div>
        ))}
      </section>

      <TextAreaField label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />

      {submitError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {submitError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center justify-end gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <Button type="submit" size="lg" disabled={!canSubmit} loading={createMutation.isPending}>
          <CheckCircle2 size={18} aria-hidden="true" /> Save receipt
        </Button>
      </div>
    </form>
  );
}
